import React, { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createUseStyles, Select } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { getEntityValue } from 'lib/services/valueLists';
import {
    applyLineSmartFieldOverridesToInputParams,
    getLineSmartFieldOverrides,
    toCellValidationError,
} from './applyLineSmartFieldOverrides';
import {
    buildMultiSelectReadOnlyPlaceholder,
    buildMultiSelectSmartFieldStateKey,
    defaultDependenceFilter,
    isEmptyMultiSelectRaw,
    mergeMultiSelectOptionsWithSelection,
    multiSelectIdsKey,
    normalizeMultiSelectValue,
    parseMultiSelectIds,
    parseMultiSelectOptions,
    resolveMultiSelectColumnLabel,
    resolveMultiSelectOnChange,
    resolveParentValueId,
    resolveRowFieldValue,
} from './multiSelectCellUtils';
import { createMultiSelectValueContainer } from './MultiSelectValuesPopover';
import { useBlurSelectOnOutsidePress } from './useBlurSelectOnOutsidePress';

import styles from './styles';

const useStyles = createUseStyles(styles, 'MultiSelectCell');

/** Minimum dropdown width when the grid column is resized narrower than the control default. */
const MULTI_SELECT_DROPDOWN_MIN_WIDTH = '240px';

const MultiSelectCell = memo((params) => {
    const { t } = useWeb5Context();
    const [value, setValue] = useState(() => normalizeMultiSelectValue(params.value));
    const [error, setError] = useState('');
    const resolvedPrimitiveRef = useRef(null);
    const pendingIdsRef = useRef(null);
    const cellRef = useRef(null);
    const selectRef = useRef(null);
    const classes = useStyles();

    const field = params.colDef.field;
    const lineOverrides = getLineSmartFieldOverrides(params.data, field);
    const resolvedInputParams = applyLineSmartFieldOverridesToInputParams(
        params.inputParams,
        lineOverrides,
    );
    const {
        onChange,
        list,
        parentField,
        parentFieldForBackend,
        isPureDependency,
        dependenceFilter,
        fieldLabel,
        ...inputParams
    } = resolvedInputParams || {};
    const isLineReadOnly = !!(lineOverrides?.readOnly || lineOverrides?.hidden);

    useBlurSelectOnOutsidePress(cellRef, selectRef, !isLineReadOnly);

    const parentValue = useMemo(() => {
        if (!parentField) return inputParams.parentValue;
        return resolveRowFieldValue(params.data, parentField);
    }, [inputParams.parentValue, parentField, params.data]);

    const parentValueId = useMemo(() => resolveParentValueId(parentValue), [parentValue]);

    const smartFieldStateKey = buildMultiSelectSmartFieldStateKey(lineOverrides);
    const selectKey = `${list || ''}-${parentValueId || ''}-${field || ''}-${smartFieldStateKey}`;

    const loadOptionsFactory = params.inputParams?.loadOptions;
    const loadOptionsFuzzyFactory = params.inputParams?.loadOptionsFuzzy;
    const isFuzzyFromParams = Boolean(params.inputParams?.isFuzzy || loadOptionsFuzzyFactory);
    const fuzzyField = params.inputParams?.field;
    const fuzzyFeature = params.inputParams?.feature;
    const excludeDisabledStates = params.inputParams?.excludeDisabledStates;

    const resolvedLoadOptions = useMemo(() => {
        if (isFuzzyFromParams && loadOptionsFuzzyFactory) {
            return loadOptionsFuzzyFactory({
                list,
                field: fuzzyField,
                feature: fuzzyFeature,
                parentField: parentFieldForBackend || parentField,
                parentFieldForBackend,
                parentValue: parentValueId,
                idParent: isPureDependency ? parentValueId : undefined,
                excludeDisabledStates,
            });
        }

        if (loadOptionsFactory) {
            const loader = loadOptionsFactory({ list });
            if (!parentField && !dependenceFilter) return loader;

            return () =>
                Promise.resolve(loader()).then((options) => {
                    const filterFn =
                        dependenceFilter ||
                        ((option) => defaultDependenceFilter(option, parentValueId));
                    return (options || []).filter(filterFn);
                });
        }

        return undefined;
    }, [
        dependenceFilter,
        excludeDisabledStates,
        fuzzyFeature,
        fuzzyField,
        isFuzzyFromParams,
        isPureDependency,
        list,
        loadOptionsFactory,
        loadOptionsFuzzyFactory,
        parentField,
        parentFieldForBackend,
        parentValueId,
    ]);

    const isFuzzyMulti = isFuzzyFromParams;

    const valueRef = useRef(value);
    valueRef.current = value;

    const wrappedLoadOptions = useMemo(() => {
        if (!resolvedLoadOptions) return undefined;

        const mergeSelection = (options) =>
            mergeMultiSelectOptionsWithSelection(options, valueRef.current);

        if (isFuzzyMulti) {
            return (text, cb) => {
                const result = resolvedLoadOptions(text, cb);
                if (result && typeof result.then === 'function') {
                    return result.then(mergeSelection);
                }
                return result;
            };
        }

        return () => Promise.resolve(resolvedLoadOptions()).then(mergeSelection);
    }, [isFuzzyMulti, resolvedLoadOptions]);

    const [preloadedOptions, setPreloadedOptions] = useState([]);

    useEffect(() => {
        if (isFuzzyMulti || !resolvedLoadOptions) {
            setPreloadedOptions([]);
            return undefined;
        }

        let cancelled = false;
        Promise.resolve(resolvedLoadOptions()).then((options) => {
            if (cancelled) return;
            setPreloadedOptions(mergeMultiSelectOptionsWithSelection(options, valueRef.current));
        });

        return () => {
            cancelled = true;
        };
    }, [isFuzzyMulti, resolvedLoadOptions, selectKey]);

    const handleOnChange = useCallback(
        (newValue, actionMeta) => {
            if (isLineReadOnly) return;
            const merged = resolveMultiSelectOnChange(value, newValue, actionMeta);
            const normalized = normalizeMultiSelectValue(merged);
            const idsKey = multiSelectIdsKey(normalized);
            pendingIdsRef.current = idsKey;
            resolvedPrimitiveRef.current = idsKey;

            const id = params.data.id || params.data.Id || params.data.ID || '';
            onChange({ rowIndex: params.node.rowIndex, id, field, value: normalized });
            setValue(normalized);
        },
        [isLineReadOnly, onChange, field, params, value],
    );

    useEffect(() => {
        const rowIndex = params.node.rowIndex;
        const rowError = inputParams?.errors?.rows?.[rowIndex]?.error;
        setError(toCellValidationError(rowError));
    }, [inputParams.errors, params]);

    useEffect(() => {
        const raw = isEmptyMultiSelectRaw(params?.value)
            ? inputParams?.defaultValue
            : params?.value;
        const incomingKey = multiSelectIdsKey(raw);

        if (pendingIdsRef.current !== null) {
            if (incomingKey === pendingIdsRef.current) {
                pendingIdsRef.current = null;
            } else {
                return;
            }
        }

        const parsed = parseMultiSelectOptions(raw);
        if (
            parsed.length &&
            parsed.every((option) => option.label && option.label !== option.value)
        ) {
            resolvedPrimitiveRef.current = incomingKey;
            setValue(normalizeMultiSelectValue(parsed));
            return;
        }

        const ids = parseMultiSelectIds(raw);
        if (!ids.length) {
            if (raw === null || raw === undefined || raw === '') {
                if (isLineReadOnly && valueRef.current?.length) {
                    return;
                }
                resolvedPrimitiveRef.current = null;
                setValue(undefined);
            }
            return;
        }

        const rawKey = ids.join('|');
        if (resolvedPrimitiveRef.current === rawKey) return;

        let cancelled = false;
        getEntityValue(list, ids).then((result) => {
            if (cancelled) return;
            if (!result?.data) return;

            const finalValue = ids
                .map((id) => {
                    const row = result.data[id] ?? result.data[String(id)];
                    if (!row) return null;
                    return {
                        label: row.description,
                        value: String(row.id),
                    };
                })
                .filter(Boolean);

            if (finalValue.length) {
                resolvedPrimitiveRef.current = rawKey;
                setValue(normalizeMultiSelectValue(finalValue));
            }
        });

        return () => {
            cancelled = true;
        };
    }, [inputParams?.defaultValue, isLineReadOnly, list, params?.value]);

    const selectValue = useMemo(() => normalizeMultiSelectValue(value), [value]);

    const resolvedFieldLabel = resolveMultiSelectColumnLabel(params, fieldLabel);
    const placeholder = t('placeholder_search');
    const emptyReadOnlyLabel = t('label_advanced_filter_option_empty');
    const placeholderReadOnly = buildMultiSelectReadOnlyPlaceholder(
        selectValue,
        emptyReadOnlyLabel,
    );

    const {
        loadOptions: _loadOptions,
        loadOptionsFuzzy: _loadOptionsFuzzy,
        isMulti: _isMulti,
        isFuzzy: _isFuzzy,
        components: _components,
        componentOverride: inputComponentOverride,
        hideMultivalueChips: _hideMultivalueChips,
        customOnChange: _customOnChange,
        ...selectInputParams
    } = resolvedInputParams || {};

    const componentOverride = useMemo(
        () => ({
            ...inputComponentOverride,
            ValueContainer: createMultiSelectValueContainer({
                fieldLabel: resolvedFieldLabel,
                isSelectLocked: isLineReadOnly,
            }),
        }),
        [inputComponentOverride, isLineReadOnly, resolvedFieldLabel],
    );

    if (!params.inputParams) return null;

    return (
        <div ref={cellRef} className={['w5-cell', classes.editableCell].join(' ')}>
            <Select
                key={selectKey}
                {...selectInputParams}
                getRef={(ref) => {
                    selectRef.current = ref;
                }}
                value={selectValue}
                onChange={handleOnChange}
                list={list}
                isMulti
                isFuzzy={isFuzzyMulti}
                alwaysLoadOnFocus={isFuzzyMulti}
                hideSelectedOptions={false}
                hideMultivalueChips
                loadOptions={wrappedLoadOptions}
                options={!isFuzzyMulti ? preloadedOptions : undefined}
                isClearable={!isLineReadOnly && (selectInputParams.isClearable ?? true)}
                componentOverride={componentOverride}
                isReadOnly={isLineReadOnly}
                readOnly={isLineReadOnly}
                placeholder={placeholder}
                placeholderReadOnly={placeholderReadOnly}
                error={error}
                isFullWidth
                selectAllLabel={
                    !isFuzzyMulti && !isLineReadOnly
                        ? inputParams.selectAllLabel || t('label_selectall')
                        : undefined
                }
                dropdownWidth={MULTI_SELECT_DROPDOWN_MIN_WIDTH}
            />
        </div>
    );
});

MultiSelectCell.displayName = 'MultiSelectCell';

export default MultiSelectCell;
